/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState, useCallback } from 'react';
import { SSEConnection } from '@/shared/utils/sseConnection';
import { PlaygroundMultiChatService } from '../services/chatBaseService';
import {
  PlaygroundSSECallbacks,
  PlaygroundSSEState,
  PlaygroundSSEEventData
} from '../types/sse';

/**
 * Hook para gerenciar conexão SSE do Knowledge Base Playground
 * Implementa chat colaborativo em tempo real com auto-reconexão
 * 
 * @param workspaceId ID do produto para conectar ao chat
 * @param chatId ID do chat para conectar
 * @param callbacks Callbacks para diferentes eventos SSE
 * @param enabled Se a conexão deve estar ativa
 * @returns Estado da conexão e funções de controle
 */
export function usePlaygroundSSE(
  workspaceId: string | null,
  chatId: string | null,
  callbacks?: PlaygroundSSECallbacks,
  enabled: boolean = true
) {
  const sseConnectionRef = useRef<SSEConnection | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const [state, setState] = useState<PlaygroundSSEState>({
    status: 'disconnected',
    retryCount: 0
  });

  // Configuração de reconexão
  const MAX_RETRY_COUNT = 5;

  /**
   * Desconectar do SSE
   */
  const disconnect = useCallback(() => {
    if (sseConnectionRef.current) {
      sseConnectionRef.current.disconnect(true);
      sseConnectionRef.current = null;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setState(prev => ({
      ...prev,
      status: 'disconnected',
      error: undefined
    }));
  }, []);

  /**
   * Conectar ao SSE
   */
  const connect = useCallback(() => {
    if (!workspaceId || !chatId || !enabled) {
      return;
    }

    // Verificar se já está conectado ou conectando para evitar loops
    if (eventSourceRef.current?.readyState === EventSource.CONNECTING ||
      eventSourceRef.current?.readyState === EventSource.OPEN) {
      return;
    }

    setState(prev => ({ ...prev, status: 'connecting' }));

    try {
      const baseUrl = PlaygroundMultiChatService.getSSEUrlWithChatId(chatId);

      // Create SSE connection with enhanced error handling
      const sseConnection = new SSEConnection({
        url: baseUrl,
        maxRetries: MAX_RETRY_COUNT,
        initialRetryDelay: 1000,
        maxRetryDelay: 16000,
        onOpen: () => {
          setState(prev => ({
            ...prev,
            status: 'connected',
            error: undefined,
            retryCount: 0,
            lastEventTime: new Date().toISOString()
          }));
          callbacks?.onConnect?.();
        },
        onError: (_event, retryCount, willRetry) => {
          setState(prev => ({
            ...prev,
            status: willRetry ? 'error' : 'disconnected',
            error: willRetry ? 'Connection failed, retrying...' : 'Max retry attempts reached',
            retryCount
          }));

          if (!willRetry) {
            callbacks?.onDisconnect?.();
          }
        },
        onMaxRetriesReached: () => {
          setState(prev => ({
            ...prev,
            status: 'disconnected',
            error: 'Unable to establish connection after multiple attempts'
          }));
          callbacks?.onDisconnect?.();
        }
      });

      sseConnectionRef.current = sseConnection;
      const eventSource = sseConnection.connect();
      eventSourceRef.current = eventSource;

      // Eventos específicos
      eventSource.addEventListener('user_message', (event: MessageEvent) => {
        try {
          const data: PlaygroundSSEEventData = JSON.parse(event.data);
          callbacks?.onUserMessage?.(data as any);
          setState(prev => ({ ...prev, lastEventTime: new Date().toISOString() }));
        } catch (error) {
          console.error('[usePlaygroundSSE] Error parsing user_message:', error);
        }
      });

      eventSource.addEventListener('ai_thinking', (event: MessageEvent) => {
        try {
          const data: PlaygroundSSEEventData = JSON.parse(event.data);
          callbacks?.onAIThinking?.(data as any);
          setState(prev => ({ ...prev, lastEventTime: new Date().toISOString() }));
        } catch (error) {
          console.error('[usePlaygroundSSE] Error parsing ai_thinking:', error);
        }
      });

      eventSource.addEventListener('ai_response', (event: MessageEvent) => {
        try {
          const data: PlaygroundSSEEventData = JSON.parse(event.data);
          callbacks?.onAIResponse?.(data as any);
          setState(prev => ({ ...prev, lastEventTime: new Date().toISOString() }));
        } catch (error) {
          console.error('[usePlaygroundSSE] Error parsing ai_response:', error);
        }
      });

      eventSource.addEventListener('ai_text_delta', (event: MessageEvent) => {
        try {
          const data: PlaygroundSSEEventData = JSON.parse(event.data);
          callbacks?.onAITextDelta?.(data as any);
        } catch (error) {
          console.error('[usePlaygroundSSE] Error parsing ai_text_delta:', error);
        }
      });

      eventSource.addEventListener('ai_text_reset', (event: MessageEvent) => {
        try {
          const data: PlaygroundSSEEventData = JSON.parse(event.data);
          callbacks?.onAITextReset?.(data as any);
        } catch (error) {
          console.error('[usePlaygroundSSE] Error parsing ai_text_reset:', error);
        }
      });

      eventSource.addEventListener('agent_selected', (event: MessageEvent) => {
        try {
          const data: PlaygroundSSEEventData = JSON.parse(event.data);
          callbacks?.onAgentSelected?.(data as any);
          setState(prev => ({ ...prev, lastEventTime: new Date().toISOString() }));
        } catch (error) {
          console.error('[usePlaygroundSSE] Error parsing agent_selected:', error);
        }
      });

      eventSource.addEventListener('tool_call_start', (event: MessageEvent) => {
        try {
          const data: PlaygroundSSEEventData = JSON.parse(event.data);
          callbacks?.onToolCallStart?.(data as any);
          setState(prev => ({ ...prev, lastEventTime: new Date().toISOString() }));
        } catch (error) {
          console.error('[usePlaygroundSSE] Error parsing tool_call_start:', error);
        }
      });

      eventSource.addEventListener('tool_call_end', (event: MessageEvent) => {
        try {
          const data: PlaygroundSSEEventData = JSON.parse(event.data);
          callbacks?.onToolCallEnd?.(data as any);
          setState(prev => ({ ...prev, lastEventTime: new Date().toISOString() }));
        } catch (error) {
          console.error('[usePlaygroundSSE] Error parsing tool_call_end:', error);
        }
      });

      eventSource.addEventListener('ai_thinking_delta', (event: MessageEvent) => {
        try {
          const data: PlaygroundSSEEventData = JSON.parse(event.data);
          callbacks?.onAIThinkingDelta?.(data as any);
        } catch (error) {
          console.error('[usePlaygroundSSE] Error parsing ai_thinking_delta:', error);
        }
      });

      eventSource.addEventListener('ai_stream_finish', (event: MessageEvent) => {
        try {
          const data: PlaygroundSSEEventData = JSON.parse(event.data);
          callbacks?.onAIStreamFinish?.(data as any);
        } catch (error) {
          console.error('[usePlaygroundSSE] Error parsing ai_stream_finish:', error);
        }
      });

      eventSource.addEventListener('bulk_items', (event: MessageEvent) => {
        try {
          const data: PlaygroundSSEEventData = JSON.parse(event.data);
          callbacks?.onBulkItems?.(data as any);
        } catch {
          // Silent catch — consistent with other SSE handlers
        }
      });

      eventSource.addEventListener('user_joined', (event: MessageEvent) => {
        try {
          const data: PlaygroundSSEEventData = JSON.parse(event.data);
          callbacks?.onUserJoined?.(data as any);
          setState(prev => ({ ...prev, lastEventTime: new Date().toISOString() }));
        } catch (error) {
          console.error('[usePlaygroundSSE] Error parsing user_joined:', error);
        }
      });

      eventSource.addEventListener('user_left', (event: MessageEvent) => {
        try {
          const data: PlaygroundSSEEventData = JSON.parse(event.data);
          callbacks?.onUserLeft?.(data as any);
          setState(prev => ({ ...prev, lastEventTime: new Date().toISOString() }));
        } catch (error) {
          console.error('[usePlaygroundSSE] Error parsing user_left:', error);
        }
      });

      // Handler para eventos de erro específicos do servidor
      eventSource.addEventListener('error', (event: MessageEvent) => {
        try {
          // Verificar se event.data existe e não é undefined
          if (event.data && event.data !== 'undefined') {
            const errorData = JSON.parse(event.data);
            console.error('[usePlaygroundSSE] Server error event:', errorData);
            callbacks?.onError?.(errorData);
          } else {
            console.warn('[usePlaygroundSSE] Error event without data:', event);
          }
        } catch (parseError) {
          console.error('[usePlaygroundSSE] Error parsing error event:', parseError);
        }
      });

    } catch (error: any) {
      console.error('[usePlaygroundSSE] Error creating EventSource:', error);
      setState(prev => ({
        ...prev,
        status: 'error',
        error: error.message
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, chatId, enabled]);

  /**
   * Reconectar manualmente
   */
  const reconnect = useCallback(() => {
    setState(prev => ({ ...prev, retryCount: 0 }));

    if (sseConnectionRef.current) {
      sseConnectionRef.current.reconnect();
    } else if (workspaceId && enabled) {
      connect();
    }
  }, [workspaceId, enabled, connect]);

  // Conectar automaticamente quando dependências mudarem
  useEffect(() => {
    const shouldConnect = enabled && workspaceId && chatId;

    if (shouldConnect) {
      connect();
    } else {
      disconnect();
    }

    // Cleanup na desmontagem
    return () => {
      disconnect();
    };
    // Removendo connect e disconnect das dependências para evitar loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, chatId, enabled]);

  return {
    status: state.status,
    error: state.error,
    retryCount: state.retryCount,
    lastEventTime: state.lastEventTime,
    isConnected: state.status === 'connected',
    isConnecting: state.status === 'connecting',
    connect,
    disconnect,
    reconnect
  };
}
